<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import LoadMoreButton from "@apps/iloom_customization/components/LoadMoreButton.svelte";
    import Pagination from "@apps/iloom_customization/components/Pagination.svelte";
    import { ProductQuery } from "@apps/iloom_customization/modules/products/queries/ProductQuery";
    import { WishlistedProductsQuery } from "@apps/iloom_customization/wishlist/queries/WishlistedProductsQuery";
    import { WishlistService } from "@apps/iloom_customization/wishlist/services/WishlistService";
    import { WISHLISTED_PRODUCTS_LIMIT } from "@apps/iloom_customization/wishlist/stores/WishlistedProductsStore";
    import { BehaviorSubject, lastValueFrom } from "rxjs";
    import { onMount } from "svelte";

    import Spinner from "@components/Spinner/Spinner.svelte";

    import WishlistProductItem from "./WishlistProductItem.svelte";

    export let productQuery: ProductQuery;
    export let wishlistedProductsQuery: WishlistedProductsQuery;
    export let wishlistService: WishlistService;

    const isMobile = window.innerWidth <= 600;

    const wishlistedProducts$ = wishlistedProductsQuery.wishlistedProducts$;
    const meta$ = wishlistedProductsQuery.meta$;
    const status$ = wishlistedProductsQuery.status$;
    const loadMoreStatus$ = wishlistedProductsQuery.loadMoreStatus$;

    const handleDeleteItem = (e: CustomEvent) => {
        lastValueFrom(wishlistService.deleteWishlistItem(e.detail));
    };

    const page$ = new BehaviorSubject<number>(1);

    const handleChangePage = (event: CustomEvent) => page$.next(event.detail);

    const getWishlistItems = (offset = 0) => {
        const params = {
            offset,
            limit: WISHLISTED_PRODUCTS_LIMIT,
        };
        lastValueFrom(wishlistService.getWishlistItems(params, isMobile));
    };

    onMount(() => {
        const changePageSubscription = page$.subscribe((value) => {
            getWishlistItems((value - 1) * WISHLISTED_PRODUCTS_LIMIT);
        });

        return () => {
            changePageSubscription.unsubscribe();
        };
    });
</script>

<div class="gw-cwl-product-list-count">
    <span>{$meta$.total}</span> products
</div>

{#if $status$ === STATUS.LOADING}
    <div class="gw-cwl-product-list-spinner"><Spinner /></div>
{/if}

{#if $status$ === STATUS.READY}
    <div class="gw-cwl-product-list">
        {#each $wishlistedProducts$ as item (new Set( [item.productId, item.variantId] ))}
            <WishlistProductItem
                {item}
                {productQuery}
                on:deleteItem={handleDeleteItem}
            />
        {/each}
    </div>
{/if}

<div class="gw-cwl-product-list-space" />

{#if isMobile}
    <LoadMoreButton
        meta={$meta$}
        loadMoreStatus={$loadMoreStatus$}
        loadMore={getWishlistItems}
    />
{:else}
    <Pagination meta={$meta$} on:changePage={handleChangePage} />
{/if}

<style lang="postcss">
    .gw-cwl-product-list-count {
        box-sizing: border-box;
        height: 100px;
        padding-top: 50px;
        margin-bottom: 48px;
        border-bottom: 1px dashed #bebebe;

        text-align: right;
        font-weight: 400;
        color: #3c3c3c;
        font-size: 14px;
        span {
            font-weight: 500;
        }
    }
    .gw-cwl-product-list-spinner {
        display: flex;
        justify-content: center;
        padding: 2rem;
    }
    .gw-cwl-product-list {
        --gw-grid-template-columns-count: 4;
        display: grid;
        grid-template-columns: repeat(
            var(--gw-grid-template-columns-count),
            1fr
        );
        column-gap: 32px;
        row-gap: 100px;
    }
    .gw-cwl-product-list-space {
        margin-bottom: 100px;
    }
    @media all and (max-width: 600px) {
        .gw-cwl-product-list-count {
            padding-top: 30px;
            margin-bottom: 16px;
            border-bottom: none;
            height: auto;
        }
        .gw-cwl-product-list {
            --gw-grid-template-columns-count: 2;
            column-gap: 10px;
            row-gap: 40px;
        }
        .gw-cwl-product-list-space {
            margin-bottom: 40px;
        }
    }
</style>
